import { MenuOutlined, UserOutlined } from '@ant-design/icons'
import { Button, Dropdown, MenuProps, theme } from 'antd'
import { Header } from 'antd/lib/layout/layout'
import { t } from 'i18next'
import { memo, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  AfterBreadcrumb,
  Breadcrumb,
  MainHeaderAuditTrails,
  MainHeaderSearch,
  Spacer,
} from '.'
import useCurrentUser from '../hooks/useCurrentUser'
import { routePaths } from '../pages/RootPage'

const { useToken } = theme

const DropDownItem = styled.div`
  width: 200px;
`

interface MainHeaderProps {
  mainMenuCollapsed: boolean
  setMainMenuCollapsed: (value: boolean) => void
  isMobile: boolean
}

const MainHeader = memo(
  ({ mainMenuCollapsed, setMainMenuCollapsed, isMobile }: MainHeaderProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { token } = useToken()

    const isSearchAvailable = useMemo(() => {
      return (
        location.pathname === routePaths.SEARCH ||
        location.pathname === routePaths.SEARCH_DOCUMENT_SUMMARIES
      )
    }, [location])

    const { currentUser, loading, handleLogout } = useCurrentUser({})

    const labelStyle = {
      cursor: 'default',
      margin: '-5px -12px',
      padding: '5px 12px',
      color: token.colorTextSecondary,
    }

    const items: MenuProps['items'] = [
      {
        key: '1',
        label: (
          <DropDownItem style={labelStyle}>
            {currentUser?.displayName}
          </DropDownItem>
        ),
        disabled: true,
      },
      {
        key: '2',
        label: (
          <DropDownItem
            onClick={() =>
              navigate(`${routePaths.USER_DETAILS}/${currentUser?.id}`)
            }
          >
            {t('mainHeader.accountMenu.myProfile')}
          </DropDownItem>
        ),
      },
      {
        key: '3',
        label: (
          <DropDownItem onClick={() => navigate(routePaths.SETTINGS)}>
            {t('mainHeader.accountMenu.settings')}
          </DropDownItem>
        ),
      },
      {
        key: '4',
        label: (
          <DropDownItem onClick={handleLogout}>
            {t('mainHeader.accountMenu.logout')}
          </DropDownItem>
        ),
      },
    ]

    return (
      <Header
        style={{
          background: token.colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 16px',
          borderRadius: '16px 0px 0px 0px',
          borderLeft: '1px solid var(--color-border, rgba(6, 5, 5, 0.15))',
          position: 'fixed',
          zIndex: 100,
          width: isMobile
            ? '100vw'
            : mainMenuCollapsed
            ? 'calc(100vw - 80px)'
            : 'calc(100vw - 200px)',
          marginLeft: isMobile ? 0 : mainMenuCollapsed ? 80 : 200,
          transition: 'all 0.2s ease',
        }}
      >
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMainMenuCollapsed(!mainMenuCollapsed)}
          />
        )}

        <Breadcrumb />
        <AfterBreadcrumb />

        {isSearchAvailable && <MainHeaderSearch loading={loading} />}
        {location.pathname === routePaths.AUDIT_TRAIL_LIST && (
          <MainHeaderAuditTrails />
        )}

        <Spacer />
        <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
          <Button
            type="default"
            size="middle"
            aria-label="account of current user"
            style={{ padding: '4px' }}
            icon={<UserOutlined />}
          />
        </Dropdown>
      </Header>
    )
  }
)

export default MainHeader
